import {
  CloseOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  MoreOutlined,
  RightOutlined,
  StarFilled,
  StarOutlined
} from "@ant-design/icons"
import { Button, Dropdown, Flex, Modal, Typography } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { downloadFile } from "../../../utils/download-file.ts"
import { FilePreviewContent } from "../../../components/FilePreviewModal/FilePreviewContent.tsx"
import type { RelatedRecord } from "../../Project/types/project-documents.types.ts"
import type { DocumentTypeOption } from "../hooks/useDocumentTypeOptions.ts"
import { type FileAlbumContext, FileDetailsPanel } from "./FileDetailsPanel.tsx"
import { type FileItem } from "../types/file-item.types.ts"

const { Text } = Typography

interface FileViewerProps {
  file: FileItem
  items: FileItem[]
  onClose: () => void
  fileActions: (file: FileItem) => MenuItemType[]
  isFavorite: (file: FileItem) => boolean
  onToggleFavorite: (file: FileItem) => void
  albumContext?: FileAlbumContext | null
  onAddToAlbum?: (file: FileItem) => void
  onRename?: (file: FileItem) => void
  onMove?: (file: FileItem) => void
  documentTypeOptions?: DocumentTypeOption[]
  documentTypesFailed?: boolean
  onChangeDocumentType?: (file: FileItem, value: string | null) => Promise<void>
  onLinkRecord?: (file: FileItem) => void
  onViewRecord?: (record: RelatedRecord) => void
}

export function FileViewer({
  file,
  items,
  onClose,
  fileActions,
  isFavorite,
  onToggleFavorite,
  albumContext = null,
  onAddToAlbum,
  onRename,
  onMove,
  documentTypeOptions,
  documentTypesFailed,
  onChangeDocumentType,
  onLinkRecord,
  onViewRecord
}: FileViewerProps) {
  const { t } = useTranslation()
  const [currentFile, setCurrentFile] = useState(file)
  const [detailsOpen, setDetailsOpen] = useState(false)

  useEffect(() => {
    setCurrentFile(file)
  }, [file.key])

  const index = items.findIndex((item) => item.key === currentFile.key)
  const hasPrev = index > 0
  const hasNext = index >= 0 && index < items.length - 1

  const goPrev = () => {
    if (hasPrev) setCurrentFile(items[index - 1])
  }
  const goNext = () => {
    if (hasNext) setCurrentFile(items[index + 1])
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && ["INPUT", "TEXTAREA"].includes(target.tagName)) return
      if (e.key === "ArrowLeft") goPrev()
      else if (e.key === "ArrowRight") goNext()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [index, items])

  const handleDownload = () => void downloadFile(currentFile.fileUrl, currentFile.name)

  const moreItems = useMemo(
    () =>
      fileActions(currentFile).filter(
        (item) => item.key !== "preview" && item.key !== "download"
      ),
    [fileActions, currentFile]
  )

  const favorited = isFavorite(currentFile)

  const relatedRecord = currentFile.relatedRecord
  return (
    <Modal
      open
      onCancel={onClose}
      closable={false}
      footer={null}
      width="100vw"
      style={{ top: 0, paddingBottom: 0, maxWidth: "100vw" }}
      styles={{
        content: { borderRadius: 0, height: "100vh", padding: 0 },
        body: { height: "100%", padding: 0 }
      }}
      destroyOnHidden
    >
      <div
        style={{
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Flex
          align="center"
          justify="space-between"
          gap="small"
          className="border-b border-border"
          style={{ padding: "10px 16px" }}
        >
          <Text
            ellipsis={{ tooltip: currentFile.name }}
            strong
            style={{ minWidth: 0, flex: 1 }}
          >
            {currentFile.name}
          </Text>
          <Flex gap="small" align="center">
            <Button
              type="text"
              icon={
                favorited ? (
                  <StarFilled className="text-warning" />
                ) : (
                  <StarOutlined />
                )
              }
              aria-label={t(favorited ? "fileViewer.unfavorite" : "fileViewer.favorite")}
              onClick={() => onToggleFavorite(currentFile)}
            />
            <Button
              icon={<DownloadOutlined />}
              aria-label={t("fileViewer.download")}
              onClick={handleDownload}
            >
              {t("fileViewer.download")}
            </Button>
            <Button
              icon={<InfoCircleOutlined />}
              aria-label={t("fileViewer.details")}
              onClick={() => setDetailsOpen((o) => !o)}
            >
              {t("fileViewer.details")}
            </Button>
            {moreItems.length > 0 && (
              <Dropdown trigger={["click"]} menu={{ items: moreItems }}>
                <Button
                  icon={<MoreOutlined />}
                  aria-label={t("fileViewer.more")}
                />
              </Dropdown>
            )}
            <Button
              type="text"
              icon={<CloseOutlined />}
              aria-label={t("fileViewer.close")}
              onClick={onClose}
            />
          </Flex>
        </Flex>

        <div style={{ position: "relative", flex: 1, overflow: "hidden" }}>
          <FilePreviewContent
            key={currentFile.key}
            fileUrl={currentFile.fileUrl}
            name={currentFile.name}
            maxHeight="calc(100vh - 130px)"
          />
          {hasPrev && (
            <Button
              shape="circle"
              size="large"
              icon={<LeftOutlined />}
              aria-label={t("fileViewer.previous")}
              onClick={goPrev}
              style={{
                position: "absolute",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)"
              }}
            />
          )}
          {hasNext && (
            <Button
              shape="circle"
              size="large"
              icon={<RightOutlined />}
              aria-label={t("fileViewer.next")}
              onClick={goNext}
              style={{
                position: "absolute",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)"
              }}
            />
          )}
        </div>

        {items.length > 1 && index >= 0 && (
          <Flex justify="center" className="border-t border-border" style={{ padding: 8 }}>
            <Text type="secondary">
              {t("fileViewer.position", { current: index + 1, total: items.length })}
            </Text>
          </Flex>
        )}

        <FileDetailsPanel
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          file={currentFile}
          albumContext={albumContext}
          onAddToAlbum={onAddToAlbum ? () => onAddToAlbum(currentFile) : undefined}
          onRename={
            currentFile.canRename && onRename
              ? () => onRename(currentFile)
              : undefined
          }
          onMove={
            currentFile.canMove && onMove ? () => onMove(currentFile) : undefined
          }
          onDownload={handleDownload}
          documentTypeOptions={documentTypeOptions}
          documentTypesFailed={documentTypesFailed}
          canEditClassification={currentFile.canMove}
          onChangeDocumentType={
            onChangeDocumentType
              ? (value) => onChangeDocumentType(currentFile, value)
              : undefined
          }
          onLinkRecord={onLinkRecord ? () => onLinkRecord(currentFile) : undefined}
          onViewRecord={
            relatedRecord && onViewRecord ? () => onViewRecord(relatedRecord) : undefined
          }
        />
      </div>
    </Modal>
  )
}
